import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import {
  StaggerWords,
  StaggerList,
  StaggerItem,
  Parallax,
  CountUp,
  DrawLine,
} from "@/components/Motion";

const VISION_IMG =
  "https://static.prod-images.emergentagent.com/jobs/6d5b6d04-c2d2-4704-9393-0ecb48523eae/images/5ddfc97cc7fa489683008e4cff362435c6f05624672a660556cc28aa2be440b5.png";

const BETS = [
  {
    k: 1,
    t: "Restoring ecosystems",
    d: "Underwriting biodiversity, water and circular industrial systems as durable infrastructure.",
    dir: -28,
  },
  {
    k: 2,
    t: "Stabilizing transitions",
    d: "Cushioning workforces, communities and supply chains through long industrial shifts.",
    dir: 28,
  },
  {
    k: 3,
    t: "Decentralizing opportunity",
    d: "Distributing energy, mobility and skill access beyond a privileged core.",
    dir: -28,
  },
  {
    k: 4,
    t: "Compounding long-term resilience",
    d: "Building institutions that accrue value across generations, not quarters.",
    dir: 28,
  },
];

const easing = [0.16, 1, 0.3, 1];

export default function Future() {
  return (
    <div data-testid="future-page" style={{ paddingTop: 168 }}>
      {/* HERO */}
      <section
        className="container-wide"
        style={{ paddingTop: "8rem", paddingBottom: "4rem" }}
      >
        <Reveal>
          <p className="label-mono">— Future / The Long-Duration Bet</p>
        </Reveal>
        <h1
          className="font-display font-bold mt-6"
          style={{
            fontSize: "clamp(2.5rem, 8vw, 7rem)",
            letterSpacing: "-0.045em",
            lineHeight: 0.92,
          }}
        >
          <StaggerWords text="The future will reward" stagger={0.05} />
          <br />
          <StaggerWords
            text="institutions capable of restoration."
            baseDelay={0.35}
            stagger={0.06}
            highlight={["capable", "of", "restoration."]}
          />
        </h1>
        <Reveal delay={0.5}>
          <p className="font-body text-xl md:text-2xl leading-snug mt-10 max-w-3xl">
            Folks Foundation is being positioned to operate on civilizational
            timescales — to absorb the volatility of industrial transition while
            compounding ecological and human value across the next century.
          </p>
        </Reveal>
      </section>

      {/* IMAGE BAND with parallax */}
      <section
        data-testid="future-imageband"
        style={{
          height: "60vh",
          overflow: "hidden",
          borderTop: "1px solid var(--soft-border)",
          borderBottom: "1px solid var(--soft-border)",
          position: "relative",
        }}
      >
        <Parallax speed={0.22}>
          <div
            style={{
              backgroundImage: `url(${VISION_IMG})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              height: "100vh",
              width: "100%",
            }}
          />
        </Parallax>
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: easing }}
          style={{
            background:
              "linear-gradient(180deg, rgba(17,18,19,0.0) 0%, rgba(17,18,19,0.5) 100%)",
          }}
        />
      </section>

      {/* THE FOUR BETS */}
      <section
        data-testid="future-bets"
        className="container-wide"
        style={{ paddingTop: "8rem", paddingBottom: "8rem" }}
      >
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <p className="label-mono md:col-span-3">— Four anchors</p>
          <h2
            className="md:col-span-9 font-display font-bold"
            style={{
              fontSize: "clamp(1.75rem, 4vw, 3.25rem)",
              letterSpacing: "-0.035em",
              lineHeight: 0.95,
            }}
          >
            <StaggerWords
              text="Institutions of the next era will be measured by their capacity to"
              stagger={0.045}
              yOffset={20}
            />{" "}
            <StaggerWords
              text="restore, stabilize, decentralize and compound."
              baseDelay={0.7}
              stagger={0.07}
              highlight={[
                "restore,",
                "stabilize,",
                "decentralize",
                "and",
                "compound.",
              ]}
            />
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {BETS.map((b, i) => (
            <motion.div
              key={b.k}
              data-testid={`future-bet-${b.k}`}
              initial={{ opacity: 0, x: b.dir, y: 16 }}
              whileInView={{ opacity: 1, x: 0, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, delay: i * 0.08, ease: easing }}
              className="border-t border-[color:var(--soft-border)] py-12 md:py-16 md:px-10"
              style={{
                borderLeft:
                  i % 2 === 1 ? "1px solid var(--soft-border)" : "none",
                background:
                  i === 1 || i === 2 ? "var(--pearl-2)" : "transparent",
              }}
            >
              <p className="pillar-num">
                <CountUp
                  to={b.k}
                  duration={1.1}
                  format={(v) => String(v).padStart(2, "0")}
                />
              </p>
              <DrawLine className="mt-2 w-12" delay={0.3} />
              <h3
                className="font-display font-bold mt-5"
                style={{
                  fontSize: "clamp(1.5rem, 3vw, 2.25rem)",
                  letterSpacing: "-0.03em",
                  lineHeight: 1,
                }}
              >
                {b.t}
              </h3>
              <p className="font-body text-lg md:text-xl leading-relaxed mt-5 max-w-md">
                {b.d}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MANIFESTO BLOCK */}
      <section
        data-testid="future-manifesto"
        style={{
          background: "var(--charcoal)",
          color: "var(--pearl)",
          padding: "10rem 0",
          overflow: "hidden",
        }}
      >
        <div className="container-wide">
          <Reveal>
            <p className="label-mono-light mb-8">— Closing manifesto</p>
          </Reveal>
          <h2
            className="font-display font-bold"
            style={{
              fontSize: "clamp(2.5rem, 9vw, 8.5rem)",
              letterSpacing: "-0.05em",
              lineHeight: 0.9,
            }}
          >
            <StaggerWords text="Regeneration is the" stagger={0.07} yOffset={48} duration={1.05} />
            <br />
            <StaggerWords
              text="next global infrastructure."
              baseDelay={0.55}
              stagger={0.09}
              yOffset={48}
              duration={1.15}
              highlight={["next", "global", "infrastructure."]}
              highlightColor="var(--mulberry-bright)"
            />
          </h2>
          <StaggerList
            className="grid md:grid-cols-12 gap-10 mt-16"
            stagger={0.15}
            delayChildren={1.4}
          >
            <StaggerItem className="md:col-span-7">
              <p className="font-body text-xl md:text-2xl leading-snug opacity-90">
                Folks Foundation is positioned to become one of those
                institutions — engineered for long horizons, sovereign-grade
                collaboration, and the compounding of ecological and human
                value over generations.
              </p>
            </StaggerItem>
            <StaggerItem className="md:col-span-5 md:justify-self-end self-end">
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="future-cta-contact"
                  className="btn-ghost-light"
                >
                  Engage the foundation <ArrowUpRight size={14} />
                </Link>
                <Link
                  to="/about"
                  data-testid="future-cta-about"
                  className="btn-ghost-light"
                >
                  About the institution
                </Link>
              </div>
            </StaggerItem>
          </StaggerList>
        </div>
      </section>
    </div>
  );
}
